import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@/app/store';

interface PromptsState {
  /** Keys of prompts closed during this page load. */
  dismissed: string[];
}

const initialState: PromptsState = { dismissed: [] };

/**
 * Which prompts have been closed, for as long as the page is open.
 *
 * <p>Deliberately not persisted. Closing a prompt means "not now", and a refresh is the natural
 * boundary for that — the alternative, remembering it forever in `localStorage`, makes an
 * accidental click permanent: the visitor never sees the offer again on that browser, no matter
 * how much later or how relevant it becomes, and nothing in the interface hints that a decision
 * with that weight was being made.
 *
 * <p>In the store rather than in component state so it outlives navigation. Clicking into an
 * event and coming back is not a new page load, and having the prompts spring back would make
 * closing them feel broken.
 */
const promptsSlice = createSlice({
  name: 'prompts',
  initialState,
  reducers: {
    promptDismissed(state, action: PayloadAction<string>) {
      if (!state.dismissed.includes(action.payload)) {
        state.dismissed.push(action.payload);
      }
    },
  },
});

export const { promptDismissed } = promptsSlice.actions;

export const selectDismissedPrompts = (state: RootState) => state.prompts.dismissed;

export default promptsSlice.reducer;
